import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Stack,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import inventoryService, {
  Inventory,
  InventorySummary,
  CreateInventoryData,
  UpdateInventoryData,
} from '../services/inventoryService';
import { productService, Product } from '../services/productService';
import { formatDate, formatDateForInput } from '../utils/date';
import {
  getStockStatusLabel,
  getStockStatusColor,
  getEmptyAddInventoryForm,
  parseInventoryQuantityInput,
  type InventoryFormData,
} from './inventory.helpers';

const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inventorySummary, setInventorySummary] = useState<InventorySummary[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);

  // URL state management
  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentSearch = searchParams.get('search') || '';
  const selectedProductId = searchParams.get('productId')
    ? parseInt(searchParams.get('productId')!)
    : null;

  // Selected product for detailed view
  const [selectedProductBatches, setSelectedProductBatches] = useState<Inventory[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedProductSummary, setSelectedProductSummary] = useState<InventorySummary | null>(
    null
  );

  // Pagination
  const [summaryTotalPages, setSummaryTotalPages] = useState(1);
  const pageSize = 20;

  // Search with debounce
  const [searchTerm, setSearchTerm] = useState(currentSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(currentSearch);

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<Inventory | null>(null);
  const [removalData, setRemovalData] = useState<{
    totalCasesToRemove: number;
    batchRemovals: { [batchId: number]: number };
  }>({
    totalCasesToRemove: 0,
    batchRemovals: {},
  });
  const [formData, setFormData] = useState<InventoryFormData>(getEmptyAddInventoryForm());
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when search or page changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (selectedProductId) params.set('productId', selectedProductId.toString());

    setSearchParams(params);
  }, [debouncedSearchTerm, currentPage, selectedProductId, setSearchParams]);

  useEffect(() => {
    loadData();
    loadProducts();
  }, []);

  useEffect(() => {
    loadInventorySummary();
  }, [currentPage, debouncedSearchTerm]);

  useEffect(() => {
    if (selectedProductId) {
      loadProductBatches(selectedProductId);
    } else {
      setSelectedProductBatches([]);
      setSelectedProductSummary(null);
    }
  }, [selectedProductId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await loadInventorySummary();
    } catch (err) {
      setError('Failed to load inventory data');
      console.error('Error loading inventory data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInventorySummary = async () => {
    try {
      const response = await inventoryService.getInventorySummary({
        page: currentPage,
        pageSize,
        productCode: debouncedSearchTerm || undefined,
      });
      setInventorySummary(response.data);
      setSummaryTotalPages(response.pagination.totalPages);
    } catch (err) {
      console.error('Error loading inventory summary:', err);
    }
  };

  const loadProductBatches = async (productId: number) => {
    setLoadingBatches(true);
    try {
      const batches = await inventoryService.getInventoryByProduct(productId);
      setSelectedProductBatches(batches);

      // Load the product's summary separately to ensure it's available even when not in filtered list
      try {
        const summaryResponse = await inventoryService.getInventorySummary({
          page: 1,
          pageSize: 100,
        });
        const productSummary = summaryResponse.data.find((s) => s.productId === productId);
        if (productSummary) {
          setSelectedProductSummary(productSummary);
        } else {
          // If not found in summary, try to get it from the batches
          if (batches.length > 0 && batches[0].product) {
            // Create a minimal summary from batch data
            const totalCases = batches.reduce((sum, b) => sum + b.casesAvailable, 0);
            const totalUnits = batches.reduce((sum, b) => sum + b.unitsAvailable, 0);
            const availableCases = batches
              .filter((b) => !b.isExpired)
              .reduce((sum, b) => sum + b.casesAvailable, 0);
            const availableUnits = batches
              .filter((b) => !b.isExpired)
              .reduce((sum, b) => sum + b.unitsAvailable, 0);

            const thresholdMinimum = batches[0].product?.thresholdMinimumCases || 0;
            const thresholdBaseline = batches[0].product?.thresholdBaselineCases || 0;

            setSelectedProductSummary({
              productId,
              product: batches[0].product!,
              totalCases,
              totalUnits,
              availableCases,
              availableUnits,
              expiredCases: totalCases - availableCases,
              expiredUnits: totalUnits - availableUnits,
              expiringSoonCases: batches
                .filter((b) => b.isExpiringSoon)
                .reduce((sum, b) => sum + b.casesAvailable, 0),
              expiringSoonUnits: batches
                .filter((b) => b.isExpiringSoon)
                .reduce((sum, b) => sum + b.unitsAvailable, 0),
              belowThreshold: availableCases <= thresholdBaseline,
              belowMinimum: availableCases <= thresholdMinimum,
              pendingProductionOrders: 0,
              status:
                availableCases === 0
                  ? 'expired'
                  : availableCases <= thresholdMinimum
                    ? 'critical'
                    : availableCases <= thresholdBaseline
                      ? 'low'
                      : 'good',
            });
          }
        }
      } catch (err) {
        console.error('Error loading product summary:', err);
      }
    } catch (err) {
      console.error('Error loading product batches:', err);
      setSelectedProductBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadProducts = async (searchTerm?: string) => {
    setLoadingProducts(true);
    try {
      if (searchTerm && searchTerm.trim()) {
        // Only show production products (includeAll defaults to false)
        const response = await productService.searchProductsPaginated(
          searchTerm,
          1,
          50,
          false,
          false
        );
        setProducts(response.products as Product[]);
      } else {
        // Only show production products (includeAll defaults to false)
        const response = await productService.getProductsPaginated(1, 50, false, false);
        setProducts(response.products as Product[]);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Debounce product search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts(productSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const handleProductSelect = (productId: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('productId', productId.toString());
    setSearchParams(params);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', page.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Reset page when searching
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleAddInventory = async () => {
    // Clear previous errors
    setFormErrors({});

    const errors: { [key: string]: string } = {};

    if (!formData.productId || formData.productId === 0) {
      errors.productId = 'Please select a product';
    }

    if (formData.casesAvailable === '') {
      errors.casesAvailable = 'Enter cases available';
    } else if (
      typeof formData.casesAvailable === 'number' &&
      Number.isNaN(formData.casesAvailable)
    ) {
      errors.casesAvailable = 'Invalid number';
    }

    if (formData.unitsAvailable === '') {
      errors.unitsAvailable = 'Enter units available';
    } else if (
      typeof formData.unitsAvailable === 'number' &&
      Number.isNaN(formData.unitsAvailable)
    ) {
      errors.unitsAvailable = 'Invalid number';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Validate dates
    if (formData.productionDate && formData.expiryDate) {
      const productionDate = new Date(formData.productionDate);
      const expiryDate = new Date(formData.expiryDate);

      if (expiryDate <= productionDate) {
        setFormErrors({
          expiryDate: 'Expiry date must be after production date',
        });
        return;
      }
    }

    const createPayload: CreateInventoryData = {
      ...formData,
      casesAvailable: formData.casesAvailable as number,
      unitsAvailable: formData.unitsAvailable as number,
    };

    try {
      await inventoryService.createInventory(createPayload);
      setSuccess('Inventory entry added successfully');
      setOpenAddDialog(false);
      resetForm();
      loadData();
      if (selectedProductId) {
        loadProductBatches(selectedProductId);
      }
    } catch (err: any) {
      // BaseApiService throws Error with message, or fallback to axios error structure
      const errorMessage =
        err.message || err.response?.data?.error || 'Failed to add inventory entry';

      // Check if it's a date validation error from backend
      if (errorMessage.includes('Expiry date must be after production date')) {
        setFormErrors({
          expiryDate: 'Expiry date must be after production date',
        });
      } else {
        setFormErrors({ general: errorMessage });
      }
    }
  };

  const handleEditInventory = async () => {
    if (!selectedInventory) return;

    try {
      const updateData: UpdateInventoryData = {
        batchNumber: formData.batchNumber,
        casesAvailable: formData.casesAvailable as number,
        unitsAvailable: formData.unitsAvailable as number,
        productionDate: formData.productionDate,
        expiryDate: formData.expiryDate,
        notes: formData.notes,
      };

      await inventoryService.updateInventory(selectedInventory.id, updateData);
      setSuccess('Inventory entry updated successfully');
      setOpenEditDialog(false);
      resetForm();
      loadData();
      if (selectedProductId) {
        loadProductBatches(selectedProductId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update inventory entry');
    }
  };

  const handleDeleteInventory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this inventory entry?')) {
      return;
    }

    try {
      await inventoryService.deleteInventory(id);
      setSuccess('Inventory entry deleted successfully');
      loadData();
      if (selectedProductId) {
        loadProductBatches(selectedProductId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete inventory entry');
    }
  };

  const resetForm = () => {
    setFormData({
      productId: 0,
      casesAvailable: 1,
      unitsAvailable: 0,
      productionDate: new Date().toISOString().split('T')[0],
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      batchNumber: '',
      notes: '',
    });
    setSelectedInventory(null);
    setProductSearchTerm('');
    setFormErrors({});
  };

  const openEditForm = (inventory: Inventory) => {
    setSelectedInventory(inventory);
    setFormData({
      productId: inventory.productId,
      casesAvailable: inventory.casesAvailable,
      unitsAvailable: inventory.unitsAvailable,
      productionDate: formatDateForInput(inventory.productionDate),
      expiryDate: formatDateForInput(inventory.expiryDate),
      batchNumber: inventory.batchNumber || '',
      notes: inventory.notes || '',
    });
    setOpenEditDialog(true);
  };

  // stock status helpers imported from './inventory.helpers'

  const getExpiryStatusColor = (inventory: Inventory) => {
    if (inventory.casesAvailable === 0) return 'default';
    if (inventory.isExpired) return 'error';
    if (inventory.isExpiringSoon) return 'warning';
    return 'success';
  };

  const getExpiryStatusLabel = (inventory: Inventory) => {
    if (inventory.casesAvailable === 0) return 'NO STOCK';
    if (inventory.isExpired) return 'EXPIRED';
    if (inventory.isExpiringSoon) return `${inventory.daysUntilExpiry}D LEFT`;
    return `${inventory.daysUntilExpiry}D LEFT`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <InventoryIcon />
            Inventory Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<WarningIcon />}
              onClick={() => navigate('/inventory-alerts')}
            >
              View Alerts
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenAddDialog(true);
              }}
              sx={{ bgcolor: '#1976d2' }}
            >
              Add Inventory
            </Button>
          </Stack>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search products..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by product name, SKU, or product code..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} fullWidth>
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Page {currentPage} of {summaryTotalPages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {inventorySummary.length} of {inventorySummary.length} products
                  {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content */}
        <Box sx={{ height: 'calc(100vh - 300px)', display: 'flex', gap: 3 }}>
          {/* Left Side - Product List */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                <Typography variant="h6">Inventory Products</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {/* Ensure selected product is always visible, even if not in filtered list */}
                {(() => {
                  const displayedSummaries = [...inventorySummary];
                  if (selectedProductId && selectedProductSummary) {
                    const isInList = displayedSummaries.some(
                      (s) => s.productId === selectedProductId
                    );
                    if (!isInList) {
                      displayedSummaries.unshift(selectedProductSummary);
                    }
                  }
                  return displayedSummaries;
                })().map((summary) => (
                  <Card
                    key={summary.productId}
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      border:
                        selectedProductId === summary.productId
                          ? '2px solid #1976d2'
                          : '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: '#1976d2',
                      },
                    }}
                    onClick={() => handleProductSelect(summary.productId)}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box>
                          <Typography variant="h6" component="h3">
                            {summary.product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {summary.product.productCode}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={getStockStatusLabel(summary)}
                            color={getStockStatusColor(summary) as any}
                            size="small"
                          />
                          <Chip
                            label={
                              (summary.pendingProductionOrders ?? 0) === 0
                                ? '0 ORDERS'
                                : `${summary.pendingProductionOrders} ORDER${
                                    (summary.pendingProductionOrders ?? 0) === 1 ? '' : 'S'
                                  }`
                            }
                            color={
                              (summary.pendingProductionOrders ?? 0) > 0 ? 'warning' : 'default'
                            }
                            size="small"
                            icon={<AssignmentIcon />}
                          />
                        </Stack>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Available Cases
                          </Typography>
                          <Typography variant="h6">{summary.availableCases}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Available Units
                          </Typography>
                          <Typography variant="h6">{summary.availableUnits}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Threshold
                          </Typography>
                          <Typography variant="body2">
                            Min: {summary.product.thresholdMinimumCases} | Target:{' '}
                            {summary.product.thresholdBaselineCases}
                          </Typography>
                        </Grid>
                        {summary.expiredCases > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="error">
                              Expired: {summary.expiredCases} cases
                            </Typography>
                          </Grid>
                        )}
                        {summary.expiringSoonCases > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="warning.main">
                              Expiring Soon: {summary.expiringSoonCases} cases
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                {inventorySummary.length === 0 && (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Products Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {debouncedSearchTerm
                        ? `No products match "${debouncedSearchTerm}"`
                        : 'No inventory data available'}
                    </Typography>
                  </Paper>
                )}

                {summaryTotalPages > 1 && (
                  <Box display="flex" flexDirection="column" alignItems="center" mt={3} gap={1}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        Page {currentPage} of {summaryTotalPages}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Showing {inventorySummary.length} products on this page
                      </Typography>
                    </Box>
                    <Pagination
                      count={summaryTotalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Right Side - Lot Details */}
          <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                <Typography variant="h6">Lot Details</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {selectedProductId ? (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Lot Details</Typography>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => setOpenRemoveDialog(true)}
                        disabled={
                          selectedProductBatches.length === 0 ||
                          selectedProductBatches.every((b) => b.casesAvailable === 0)
                        }
                      >
                        Remove Inventory
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {loadingBatches ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    ) : selectedProductBatches.length === 0 ? (
                      <Typography color="text.secondary" textAlign="center" py={3}>
                        No inventory lots found for this product
                      </Typography>
                    ) : (
                      <List>
                        {selectedProductBatches.map((batch, index) => (
                          <React.Fragment key={batch.id}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1">
                                      Lot: {batch.batchNumber || 'No Lot #'}
                                    </Typography>
                                    {batch.productionOrderId && (
                                      <Link
                                        to={`/production-orders?orderId=${batch.productionOrderId}`}
                                        style={{
                                          textDecoration: 'none',
                                          color: '#1976d2',
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        PO #{batch.productionOrderId}
                                      </Link>
                                    )}
                                    <Stack direction="row" spacing={1}>
                                      <Chip
                                        label={getStockStatusLabel(
                                          selectedProductSummary ||
                                            inventorySummary.find(
                                              (s) => s.productId === selectedProductId
                                            )
                                        )}
                                        color={
                                          getStockStatusColor(
                                            selectedProductSummary ||
                                              inventorySummary.find(
                                                (s) => s.productId === selectedProductId
                                              )
                                          ) as any
                                        }
                                        size="small"
                                      />
                                      <Chip
                                        label={getExpiryStatusLabel(batch)}
                                        color={getExpiryStatusColor(batch) as any}
                                        size="small"
                                      />
                                    </Stack>
                                  </Box>
                                }
                                secondary={
                                  <Box mt={1}>
                                    <Typography variant="body2">
                                      <strong>Cases:</strong> {batch.casesAvailable} |
                                      <strong> Units:</strong> {batch.unitsAvailable}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Production:</strong>{' '}
                                      {formatDate(batch.productionDate)} |<strong> Expiry:</strong>{' '}
                                      {formatDate(batch.expiryDate)}
                                    </Typography>
                                    {batch.product?.storageRequirements && (
                                      <Typography variant="body2">
                                        <strong>Storage:</strong>{' '}
                                        {batch.product.storageRequirements}
                                      </Typography>
                                    )}
                                    {batch.notes && (
                                      <Typography variant="body2" color="text.secondary">
                                        {batch.notes}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => openEditForm(batch)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteInventory(batch.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                            {/* Adjustments Accordion */}
                            {batch.adjustments && batch.adjustments.length > 0 && (
                              <Accordion>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls={`adjustments-${batch.id}-content`}
                                  id={`adjustments-${batch.id}-header`}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <HistoryIcon fontSize="small" />
                                    <Typography variant="body2">
                                      Inventory Adjustments ({batch.adjustments.length})
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Date</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Before</TableCell>
                                          <TableCell>After</TableCell>
                                          <TableCell>Change</TableCell>
                                          <TableCell>Reason</TableCell>
                                          <TableCell>User</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {batch.adjustments.map((adjustment) => (
                                          <TableRow key={adjustment.id}>
                                            <TableCell>
                                              {formatDate(adjustment.createdAt)}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={adjustment.adjustmentType
                                                  .replace('_', ' ')
                                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                                                size="small"
                                                color={
                                                  adjustment.adjustmentType.includes('add')
                                                    ? 'success'
                                                    : adjustment.adjustmentType.includes(
                                                          'subtract'
                                                        ) ||
                                                        adjustment.adjustmentType.includes('deduct')
                                                      ? 'error'
                                                      : 'default'
                                                }
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {adjustment.casesBefore} cases,{' '}
                                              {adjustment.unitsBefore} units
                                            </TableCell>
                                            <TableCell>
                                              {adjustment.casesAfter} cases, {adjustment.unitsAfter}{' '}
                                              units
                                            </TableCell>
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                color={
                                                  adjustment.casesAdjusted >= 0 &&
                                                  adjustment.unitsAdjusted >= 0
                                                    ? 'success.main'
                                                    : 'error.main'
                                                }
                                              >
                                                {adjustment.casesAdjusted >= 0 ? '+' : ''}
                                                {adjustment.casesAdjusted} cases,{' '}
                                                {adjustment.unitsAdjusted >= 0 ? '+' : ''}
                                                {adjustment.unitsAdjusted} units
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              {adjustment.reason || '-'}
                                              {adjustment.notes && (
                                                <Tooltip title={adjustment.notes}>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ ml: 1, cursor: 'help' }}
                                                  >
                                                    ℹ️
                                                  </Typography>
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {adjustment.adjustedByUser
                                                ? adjustment.adjustedByUser.username
                                                : 'System'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            )}
                            {index < selectedProductBatches.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={8}
                  >
                    <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Select a Product
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Click on a product from the inventory list to view detailed lot information
                      and manage individual inventory entries.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Add Inventory Dialog */}
        <Dialog
          open={openAddDialog}
          onClose={() => {
            setOpenAddDialog(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add Inventory Entry</DialogTitle>
          <DialogContent>
            {formErrors.general && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setFormErrors({ ...formErrors, general: '' })}
              >
                {formErrors.general}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={products}
                  loading={loadingProducts}
                  value={products.find((p) => p.id === formData.productId) || null}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, productId: newValue?.id || 0 });
                    if (formErrors.productId) {
                      setFormErrors({ ...formErrors, productId: '' });
                    }
                  }}
                  getOptionLabel={(option) => `${option.name} (${option.product_code})`}
                  onInputChange={(_, newInputValue, reason) => {
                    // Only update search term when user is typing, not when selecting/closing/resetting
                    // This prevents infinite loops when selecting a product
                    if (reason === 'input') {
                      setProductSearchTerm(newInputValue);
                    } else if (reason === 'clear') {
                      // Clear search term when user clears the input
                      setProductSearchTerm('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      placeholder="Start typing to search products..."
                      error={!!formErrors.productId}
                      helperText={formErrors.productId}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProducts ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cases Available"
                  type="number"
                  value={formData.casesAvailable}
                  onChange={(e) => {
                    const casesAvailable = parseInventoryQuantityInput(e.target.value);
                    setFormData({ ...formData, casesAvailable });
                    if (formErrors.casesAvailable) {
                      setFormErrors({ ...formErrors, casesAvailable: '' });
                    }
                  }}
                  error={!!formErrors.casesAvailable}
                  helperText={formErrors.casesAvailable}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Units Available"
                  type="number"
                  value={formData.unitsAvailable}
                  onChange={(e) => {
                    const unitsAvailable = parseInventoryQuantityInput(e.target.value);
                    setFormData({ ...formData, unitsAvailable });
                    if (formErrors.unitsAvailable) {
                      setFormErrors({ ...formErrors, unitsAvailable: '' });
                    }
                  }}
                  error={!!formErrors.unitsAvailable}
                  helperText={formErrors.unitsAvailable}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Production Date"
                  type="date"
                  value={formData.productionDate}
                  onChange={(e) => {
                    setFormData({ ...formData, productionDate: e.target.value });
                    // Clear error when user changes date
                    if (formErrors.expiryDate) {
                      setFormErrors({ ...formErrors, expiryDate: '' });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => {
                    setFormData({ ...formData, expiryDate: e.target.value });
                    // Clear error when user changes date
                    if (formErrors.expiryDate) {
                      setFormErrors({ ...formErrors, expiryDate: '' });
                    }
                  }}
                  error={!!formErrors.expiryDate}
                  helperText={formErrors.expiryDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Lot Number"
                  value={formData.batchNumber}
                  onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                resetForm();
                setOpenAddDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddInventory} variant="contained">
              Add Inventory
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Inventory Dialog */}
        <Dialog
          open={openRemoveDialog}
          onClose={() => setOpenRemoveDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Remove Inventory</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {/* Fixed Header Section */}
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', bgcolor: 'grey.50' }}>
              {/* Summary Information with Inline Input */}
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total to Remove
                    </Typography>
                    <TextField
                      type="number"
                      value={removalData.totalCasesToRemove}
                      onChange={(e) => {
                        const total = Number(e.target.value);
                        setRemovalData({
                          totalCasesToRemove: total,
                          batchRemovals: {},
                        });
                      }}
                      inputProps={{
                        min: 0,
                        max: selectedProductBatches.reduce(
                          (sum, batch) => sum + batch.casesAvailable,
                          0
                        ),
                      }}
                      size="small"
                      fullWidth
                      placeholder="Enter cases"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Selected
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {Object.values(removalData.batchRemovals).reduce(
                        (sum, val) => sum + (val || 0),
                        0
                      )}{' '}
                      cases
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Remaining to Allocate
                    </Typography>
                    <Typography variant="h6" color="error">
                      {removalData.totalCasesToRemove -
                        Object.values(removalData.batchRemovals).reduce(
                          (sum, val) => sum + (val || 0),
                          0
                        )}{' '}
                      cases
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Scrollable Lot List */}
            {removalData.totalCasesToRemove > 0 && (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto', p: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Distribute the {removalData.totalCasesToRemove} cases across the available lots:
                </Typography>

                {selectedProductBatches
                  .filter((batch) => batch.casesAvailable > 0)
                  .map((batch) => (
                    <Box
                      key={batch.id}
                      sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1">
                          Lot: {batch.batchNumber || 'No Lot #'}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={getExpiryStatusLabel(batch)}
                            color={getExpiryStatusColor(batch) as any}
                            size="small"
                          />
                        </Stack>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Available: {batch.casesAvailable} cases | Expiry:{' '}
                        {formatDate(batch.expiryDate)}
                        {batch.product?.storageRequirements &&
                          ` | Storage: ${batch.product.storageRequirements}`}
                      </Typography>
                      <TextField
                        fullWidth
                        label="Cases to Remove from this Lot"
                        type="number"
                        value={removalData.batchRemovals[batch.id] || 0}
                        onChange={(e) => {
                          const amount = Number(e.target.value);
                          setRemovalData((prev) => ({
                            ...prev,
                            batchRemovals: {
                              ...prev.batchRemovals,
                              [batch.id]: amount,
                            },
                          }));
                        }}
                        inputProps={{ min: 0, max: batch.casesAvailable }}
                        size="small"
                      />
                    </Box>
                  ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenRemoveDialog(false);
                setRemovalData({ totalCasesToRemove: 0, batchRemovals: {} });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  const totalSelected = Object.values(removalData.batchRemovals).reduce(
                    (sum, val) => sum + (val || 0),
                    0
                  );

                  if (totalSelected !== removalData.totalCasesToRemove) {
                    setError(
                      `Please allocate exactly ${removalData.totalCasesToRemove} cases across lots. Currently allocated: ${totalSelected}`
                    );
                    return;
                  }

                  // Process each batch removal
                  for (const [batchId, casesToRemove] of Object.entries(
                    removalData.batchRemovals
                  )) {
                    if (casesToRemove && casesToRemove > 0) {
                      const batch = selectedProductBatches.find((b) => b.id === Number(batchId));
                      if (batch) {
                        const unitsPerCase = batch.unitsAvailable / batch.casesAvailable;
                        const unitsToRemove = casesToRemove * unitsPerCase;

                        await inventoryService.adjustInventory(Number(batchId), {
                          casesAdjusted: -casesToRemove,
                          unitsAdjusted: -Math.round(unitsToRemove),
                          reason: 'Warehouse removal',
                          notes: `Removed ${casesToRemove} cases from lot ${batch.batchNumber || 'No Lot #'}`,
                        });
                      }
                    }
                  }

                  setSuccess(
                    `Successfully removed ${removalData.totalCasesToRemove} cases from inventory`
                  );
                  setOpenRemoveDialog(false);
                  setRemovalData({ totalCasesToRemove: 0, batchRemovals: {} });
                  loadData();
                  if (selectedProductId) {
                    loadProductBatches(selectedProductId);
                  }
                } catch (err: any) {
                  setError(err.response?.data?.error || 'Failed to remove inventory');
                }
              }}
              variant="contained"
              color="warning"
              disabled={
                removalData.totalCasesToRemove === 0 ||
                Object.values(removalData.batchRemovals).reduce(
                  (sum, val) => sum + (val || 0),
                  0
                ) !== removalData.totalCasesToRemove
              }
            >
              Remove Inventory
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Inventory Dialog */}
        <Dialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            Edit Inventory Entry
            <IconButton
              aria-label="close"
              onClick={() => setOpenEditDialog(false)}
              sx={{ color: 'grey.500' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cases Available"
                  type="number"
                  value={formData.casesAvailable}
                  onChange={(e) =>
                    setFormData({ ...formData, casesAvailable: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Units Available"
                  type="number"
                  value={formData.unitsAvailable}
                  onChange={(e) =>
                    setFormData({ ...formData, unitsAvailable: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Production Date"
                  type="date"
                  value={formData.productionDate}
                  onChange={(e) => setFormData({ ...formData, productionDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Lot Number"
                  value={formData.batchNumber}
                  onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditInventory} variant="contained">
              Update Inventory
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default InventoryPage;
